/**
 * Operations Kanban Summary Bar (QUAL-5914 + QUAL-5923)
 *
 * Single summary strip at the top of the Operations Kanban:
 *  - headline = weighted pipeline: Σ (each active stage's card value × that
 *    stage's valuePercent), Done excluded — full-dollar precision;
 *  - the $ total (and count) of quotes by commercial status
 *    (Approved / In Progress / Invoiced);
 *  - a From…To window over quote CREATION date.
 *
 * ONE window drives everything (see dao/quotes-v4.js getKanban): the board
 * below, the status tiles, and the weighted figure all reflect quotes created
 * in the window. Either bound may be left empty for an open-ended window.
 */

import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatCompactCurrency } from '@/lib/utils';
import type { QuoteStatusTotal } from '../types';
import type { WeightedPipelineResult } from '../weightedPipeline';

const STATUS_TILES = [
  { status: 'approved', labelKey: 'stats.approved', fallback: 'Approved', dot: 'bg-blue-500' },
  { status: 'inprogress', labelKey: 'stats.inProgress', fallback: 'In Progress', dot: 'bg-orange-500' },
  { status: 'invoiced', labelKey: 'stats.invoiced', fallback: 'Invoiced', dot: 'bg-green-500' },
] as const;

interface OperationsKanbanSummaryBarProps {
  weighted: WeightedPipelineResult;
  summary?: QuoteStatusTotal[];
  fromDate?: string;
  toDate?: string;
  onRangeChange: (fromDate?: string, toDate?: string) => void;
  loading?: boolean;
}

export function OperationsKanbanSummaryBar({
  weighted,
  summary,
  fromDate,
  toDate,
  onRangeChange,
  loading = false,
}: OperationsKanbanSummaryBarProps) {
  const { t } = useTranslation('quotes');
  const entryFor = (status: string) => summary?.find((s) => s.status === status);
  const hasRange = Boolean(fromDate || toDate);
  const activeStages = weighted.byStage.filter((s) => s.count > 0);

  return (
    <div className="rounded-lg border p-4 space-y-3">
      {/* Weighted pipeline headline + creation-date window */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tracking-tight text-foreground">
                {loading ? '—' : formatCurrency(weighted.total, weighted.currency, 0)}
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                {t('operationsKanban.weightedPipeline', 'weighted pipeline')}
              </span>
            </div>
            {!loading && (
              <p className="text-xs text-muted-foreground">
                {weighted.count} {t('operationsKanban.inProgress', 'in progress')}
                {' · '}
                {t('operationsKanban.rawValue', 'raw value')}{' '}
                {formatCurrency(weighted.rawTotal, weighted.currency, 0)}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('operationsKanban.createdFrom', 'Created from')}
            <Input
              type="date"
              value={fromDate ?? ''}
              max={toDate || undefined}
              onChange={(e) => onRangeChange(e.target.value || undefined, toDate)}
              className="h-9 w-40"
              aria-label={t('operationsKanban.createdFrom', 'Created from')}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('operationsKanban.createdTo', 'Created to')}
            <Input
              type="date"
              value={toDate ?? ''}
              min={fromDate || undefined}
              onChange={(e) => onRangeChange(fromDate, e.target.value || undefined)}
              className="h-9 w-40"
              aria-label={t('operationsKanban.createdTo', 'Created to')}
            />
          </label>
          {(fromDate || toDate) && (
            <Button variant="ghost" size="sm" onClick={() => onRangeChange(undefined, undefined)}>
              {t('filters.clearColumnFilters', 'Clear')}
            </Button>
          )}
        </div>
      </div>

      {/* Status $ tiles over the same creation window */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t pt-3">
        {STATUS_TILES.map((tile) => {
          const entry = entryFor(tile.status);
          return (
            <div key={tile.status} className="flex items-center gap-2">
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${tile.dot}`} aria-hidden />
              <div>
                <div className="text-xs text-muted-foreground">{t(tile.labelKey, tile.fallback)}</div>
                <div className="text-lg font-bold leading-tight">
                  {loading ? '—' : formatCurrency(entry?.amount ?? 0, 'CAD', 0)}
                  {!loading && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({entry?.count ?? 0})
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-stage weighted breakdown */}
      {!loading && activeStages.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t pt-3">
          {activeStages.map((s) => (
            <span
              key={s.stage}
              title={`${s.label}: ${formatCurrency(s.rawValue, weighted.currency, 0)} × ${s.valuePercent}% = ${formatCurrency(s.weightedValue, weighted.currency, 0)} (${s.count})`}
              className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs"
            >
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-semibold text-primary">{formatCompactCurrency(s.weightedValue)}</span>
              <span className="text-[10px] text-muted-foreground">({s.valuePercent}%)</span>
            </span>
          ))}
        </div>
      )}

      {hasRange && (
        <p className="text-xs text-muted-foreground">
          {t(
            'operationsKanban.rangeHint',
            'The board, status totals, and weighted pipeline reflect quotes created in this range.',
          )}
        </p>
      )}
    </div>
  );
}
